/**
 * RejectionDialog Component
 *
 * Reusable dialog for rejecting FAQ submissions with a reason.
 * Extracted from ApprovalActions.tsx for better separation of concerns.
 *
 * Features:
 * - Text field for rejection reason (required)
 * - Validation - submit button disabled if reason is empty
 * - Loading state support
 * - Keyboard navigation support
 * - Helper text for user guidance
 *
 * Usage:
 * ```tsx
 * const [open, setOpen] = useState(false);
 * const [reason, setReason] = useState('');
 *
 * <RejectionDialog
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   onConfirm={handleReject}
 *   faqId={faq.document_id}
 * />
 * ```
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface RejectionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  faqId: string;
  isSubmitting?: boolean;
}

export const RejectionDialog: React.FC<RejectionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  faqId,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const [rejectionReason, setRejectionReason] = useState('');

  // Reset reason when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setRejectionReason('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (rejectionReason.trim()) {
      onConfirm(rejectionReason);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && event.ctrlKey && rejectionReason.trim()) {
      handleConfirm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="rejection-dialog-title"
    >
      <DialogTitle id="rejection-dialog-title">
        {t('faq.actions.reject_dialog_title')}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t('faq.actions.rejection_reason')}
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          onKeyPress={handleKeyPress}
          helperText={t('faq.actions.rejection_reason_help')}
          disabled={isSubmitting}
          placeholder={t('faq.actions.rejection_reason_placeholder', 'Please provide a reason for rejection...')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={isSubmitting || !rejectionReason.trim()}
        >
          {isSubmitting ? t('common.submitting') : t('faq.actions.reject')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
